import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

import { SecondaryTextButton, TextButton } from '@bibliocommons/base-buttons';
import {
  ChevronDown as IconDown,
  ChevronUp as IconUp,
  Close as IconClose,
  Plus as IconPlus
} from '@bibliocommons/base-icons';
import ScreenReaderMessage from '@bibliocommons/base-screen-reader-message';
import { Table, TableHeader, TableHeading, TableBody, TableRow, TableCell } from '@bibliocommons/table';
import ToggleSwitch from '@bibliocommons/toggle-switch';

import { MOVE_CATEGORY_DIRECTION } from 'app/constants/AdminBrowseConstants';
import { categoryShape } from 'app/shapes/browse';

import './CategoryListEditor.scss';

const CategoryValueInput = ({ onChange, initialValue }) => {
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    onChange(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleChange = event => {
    setValue(event.target.value);
  };

  return <input onChange={handleChange} type="text" value={value} />;
};

CategoryValueInput.propTypes = {
  onChange: PropTypes.func.isRequired,
  initialValue: PropTypes.string.isRequired
};

const CategoryListEditor = ({
  categories,
  deleteCategory,
  moveCategory,
  onAddCategoryClick,
  parentCategoryId,
  setCategorySearchUrl,
  setCategoryTitle,
  toggleIsEnabled
}) => {
  const handleAddCategoryClick = () => {
    onAddCategoryClick(parentCategoryId);
  };

  return (
    <div className="cp-category-list-editor">
      <Table>
        <TableHeader>
          <TableHeading>Label</TableHeading>
          <TableHeading>URL</TableHeading>
          <TableHeading>Enabled</TableHeading>
          <TableHeading>Remove</TableHeading>
          <TableHeading></TableHeading>
          <TableHeading></TableHeading>
        </TableHeader>
        <TableBody>
          {categories.map((category, index) => {
            const { categoryId, isEnabled, searchUrl, title } = category;

            const canMoveDown = index < categories.length - 1;
            const canMoveUp = index > 0;

            return (
              <TableRow key={categoryId}>
                <TableCell>
                  <CategoryValueInput
                    onChange={categoryTitle => setCategoryTitle(categoryId, categoryTitle)}
                    initialValue={title}
                  />
                </TableCell>
                <TableCell>
                  <CategoryValueInput
                    onChange={categorySearchUrl => setCategorySearchUrl(categoryId, categorySearchUrl)}
                    initialValue={searchUrl}
                  />
                </TableCell>
                <TableCell>
                  <div className="cell-centered">
                    <ToggleSwitch
                      active={isEnabled}
                      activeText="Yes"
                      handleClick={() => toggleIsEnabled(categoryId)}
                      inactiveText="No"
                    />
                  </div>
                </TableCell>
                <TableCell className="cell-icon-only">
                  <div className="cell-centered">
                    <TextButton
                      className="icon-button"
                      dataKey="remove-category"
                      handleClick={() => deleteCategory(categoryId)}
                      type="button"
                    >
                      <IconClose />
                      <ScreenReaderMessage>Remove category</ScreenReaderMessage>
                    </TextButton>
                  </div>
                </TableCell>
                <TableCell className="cell-icon-only">
                  {canMoveUp && (
                    <div className="cell-centered">
                      <TextButton
                        className="icon-button"
                        dataKey="move-up-category"
                        handleClick={() => moveCategory(categoryId, MOVE_CATEGORY_DIRECTION.UP)}
                        type="button"
                      >
                        <IconUp />
                        <ScreenReaderMessage>Move category up</ScreenReaderMessage>
                      </TextButton>
                    </div>
                  )}
                </TableCell>
                <TableCell className="cell-icon-only">
                  {canMoveDown && (
                    <div className="cell-centered">
                      <TextButton
                        className="icon-button"
                        dataKey="move-down-category"
                        handleClick={() => moveCategory(categoryId, MOVE_CATEGORY_DIRECTION.DOWN)}
                        type="button"
                      >
                        <IconDown />
                        <ScreenReaderMessage>Move category down</ScreenReaderMessage>
                      </TextButton>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <SecondaryTextButton
        className="add-category"
        dataKey="add-category"
        handleClick={handleAddCategoryClick}
        leftAligned
        renderLeadingIcon={() => <IconPlus className="icon-plus" />}
        type="button"
      >
        Add at this level
      </SecondaryTextButton>
    </div>
  );
};

CategoryListEditor.propTypes = {
  categories: PropTypes.arrayOf(categoryShape).isRequired,
  deleteCategory: PropTypes.func.isRequired,
  moveCategory: PropTypes.func.isRequired,
  parentCategoryId: PropTypes.string.isRequired,
  onAddCategoryClick: PropTypes.func.isRequired,
  setCategorySearchUrl: PropTypes.func.isRequired,
  setCategoryTitle: PropTypes.func.isRequired,
  toggleIsEnabled: PropTypes.func.isRequired
};

export default CategoryListEditor;
